import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-auth';
import { findActiveChild, type NavItem } from '@/lib/navigation';
import { cn } from '@/lib/utils';

/** Abas horizontais com os subitens do módulo atual (estilo Metronic). Rola horizontalmente. */
export function ModuleTabs({ module, className }: { module: NavItem; className?: string }) {
  const { canAny } = usePermissions();
  const { pathname } = useLocation();
  const ref = useRef<HTMLElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const tabs = module.children?.filter((c) => !c.permissions || canAny(...c.permissions)) ?? [];
  const active = findActiveChild(pathname, tabs);

  // Mantém a aba ativa visível e indica (com fade) que há abas escondidas para rolar.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () =>
      setOverflow({
        left: el.scrollLeft > 2,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
      });
    const revealActive = () =>
      el.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    revealActive();
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(() => {
      revealActive();
      update();
    });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [active?.to, tabs.length]);

  if (tabs.length === 0) return null;
  return (
    <div className={cn('relative min-w-0', className)}>
      <nav
        ref={ref}
        aria-label={`Seções de ${module.label}`}
        className="flex h-full items-stretch gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const isActive = active?.to === tab.to;
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                '-mb-px flex shrink-0 items-center border-b-2 px-3 text-sm whitespace-nowrap transition-colors outline-none focus-visible:bg-muted',
                isActive
                  ? 'border-primary font-medium text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border-strong hover:text-foreground',
              )}
            >
              {tab.label}
            </NavLink>
          );
        })}
      </nav>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-card to-transparent transition-opacity',
          overflow.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-card to-transparent transition-opacity',
          overflow.right ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
